import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Brotautomat & Backwarenautomat“ (/brotautomat).
 *
 * Bündelt brot + brötchen + backwaren auf EINER Seite (keine dünnen Doorways).
 * Eigener Winkel: Umsatz in den RANDZEITEN (früh morgens, sonntags, feiertags)
 * ohne Ladendienst – das Kernargument für Bäckereien. Referenz: Brotfreundin
 * (Live-Link + Video). Trust über echte Presseberichte zur marktbox/Brotbox.
 */
@Component({
  selector: 'app-brotautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './brotautomat-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class BrotautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  /** Ratgeber-Zubringer (informationaler Intent) rund um Bäckerei/Backwaren. */
  readonly blogBaeckerei = 'verkaufsautomat-fuer-baeckereien';
  readonly blogBackwaren = 'verkaufsautomat-fuer-backwaren';
  readonly blogUmsatz = 'wie-ein-baecker-mit-verkaufsautomaten-die-umsaetze-steigert';

  /** Öffentliche Live-Box: Brotfreundin (Simone Krieg-Hornung). */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/simone-krieg-hornung/U-MB-00230'
  );

  /** YouTube-Facade (lädt erst nach Klick): Betreiberin „Brotfreundin“. */
  readonly videoThumb = 'https://i.ytimg.com/vi/KiDM7zt3k6A/hqdefault.jpg';
  videoOpen = false;
  videoUrl?: SafeResourceUrl;

  /** Echte externe Berichte zur marktbox/Brotbox → Trust-Signale. */
  readonly press: { label: string; url: string }[] = [
    { label: 'tonight.de – 24/7-Brotautomat in Düsseldorf', url: 'https://www.tonight.de/duesseldorf/brotautomat-in-duesseldorf-baeckerei-behmer-bietet-jetzt-24-7-rund-um-die-uhr-frisches-brot-in-lohausen_396323.html' },
    { label: 'NDR – Bäckerei aus Stapel geht mit Bäckerboxen neue Wege', url: 'https://www.ndr.de/nachrichten/schleswig-holstein/rettung-aus-der-insolvenz-baeckerei-aus-stapel-geht-neue-wege,baeckerboxen-100.html' },
    { label: 'brotundbackwaren.de – Brot aus dem Automaten', url: 'https://brotundbackwaren.de/brot-aus-dem-automaten-backerei-van-densens-brood-doosken/' },
  ];

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@brot.faq1.q:Kann ich mit einem Brotautomaten auch sonntags verkaufen?`,
      a: $localize`:@@brot.faq1.a:Ja – genau das ist der Vorteil. Die marktbox verkauft rund um die Uhr, auch früh morgens, sonntags und an Feiertagen, ohne dass jemand im Laden stehen muss.`,
    },
    {
      q: $localize`:@@brot.faq2.q:Bleiben Brot und Brötchen im Automaten frisch?`,
      a: $localize`:@@brot.faq2.a:Die Ware liegt in einzeln verschlossenen Klappenfächern und wird von dir nach Bedarf bestückt. So verkaufst du frische Backwaren aus der eigenen Produktion – du steuerst Menge und Zeitpunkt selbst.`,
    },
    {
      q: $localize`:@@brot.faq3.q:Welche Fächergrößen gibt es für Brote und Körbe?`,
      a: $localize`:@@brot.faq3.a:Die marktbox gibt es mit 24 oder 39 Fächern und lässt sich mit Erweiterungen und Zubehör an dein Sortiment anpassen – von Brötchentüten bis zu großen Broten.`,
    },
    {
      q: $localize`:@@brot.faq4.q:Was kostet ein Backwarenautomat?`,
      a: $localize`:@@brot.faq4.a:Ab 6.800 € zum Kauf oder ab 300 € pro Monat zur Miete – je nach Modell und Zubehör. Wie sich das für eine Bäckerei rechnet, zeigt unser Ratgeber.`,
    },
  ];

  openVideo(): void {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube-nocookie.com/embed/KiDM7zt3k6A?autoplay=1&rel=0'
    );
    this.videoOpen = true;
  }

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@brot.metaTitle:Brotautomat & Backwarenautomat kaufen | marktbox`;
    const description =
      $localize`:@@brot.metaDesc:Brotautomat & Backwarenautomat kaufen oder mieten: Brot & Brötchen 24/7 verkaufen – auch sonntags. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content:
        $localize`:@@brot.metaKeywords:Brotautomat, Brötchenautomat, Backwarenautomat, Brot verkaufen Automat, Bäckerei Automat, Brotbox, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('brotautomat-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Brot- & Backwarenautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('brotautomat-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    this.injectJsonLd('brotautomat-video-schema', {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'marktbox – Von der Idee zur eigenen Backstube: Brotfreundin über echtes Bäckerhandwerk',
      description:
        'Einblick in die Backstube der „Brotfreundin": echtes Bäckerhandwerk – und wie sie ihre Backwaren über die marktbox rund um die Uhr verkauft.',
      thumbnailUrl: 'https://i.ytimg.com/vi/KiDM7zt3k6A/hqdefault.jpg',
      uploadDate: '2026-07-17',
      duration: 'PT3M49S',
      embedUrl: 'https://www.youtube-nocookie.com/embed/KiDM7zt3k6A',
      contentUrl: 'https://www.youtube.com/watch?v=KiDM7zt3k6A',
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/brotautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
